import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue, push, update, remove } from "../../Config/matuRtdb";
import {
  Bell,
  BellOff,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Clock,
  Users,
  MessageCircle,
  AlertTriangle,
  CheckCircle,
  Settings,
  Zap,
  Target,
  Calendar,
  Mail,
  Smartphone,
  Sparkles,
  TrendingUp,
  Eye,
  EyeOff
} from "lucide-react";

export default function NotificationManager() {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "info",
    target: "all",
    schedule: "immediate",
    scheduledTime: "",
    conditions: {
      userType: "all",
      minBalance: "",
      isVip: false,
      isActive: true
    }
  });
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    pending: 0,
    today: 0
  });
  const db = getDatabase();

  useEffect(() => {
    loadNotifications();
    loadNotificationStats();
  }, []);

  const loadNotifications = () => {
    const notificationsRef = ref(db, "notifications");
    onValue(notificationsRef, (snapshot) => {
      const notificationsData = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          notificationsData.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        notificationsData.sort((a, b) => b.createdAt - a.createdAt);
        setNotifications(notificationsData);
      }
      setIsLoading(false);
    });
  };

  const loadNotificationStats = () => {
    const notificationsRef = ref(db, "notifications");
    onValue(notificationsRef, (snapshot) => {
      if (snapshot.exists()) {
        const today = new Date().setHours(0, 0, 0, 0);
        let total = 0;
        let sent = 0;
        let pending = 0;
        let todayCount = 0;

        snapshot.forEach((childSnapshot) => {
          const notification = childSnapshot.val();
          total++;

          if (notification.status === "sent") {
            sent++;
          } else if (notification.status === "pending") {
            pending++;
          }

          if (notification.createdAt >= today) {
            todayCount++;
          }
        });

        setStats({ total, sent, pending, today: todayCount });
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const notificationData = {
        ...formData,
        createdAt: Date.now(),
        status: formData.schedule === "immediate" ? "pending" : "scheduled",
        sentCount: 0,
        readCount: 0,
        clickCount: 0
      };

      if (editingNotification) {
        const notificationRef = ref(db, `notifications/${editingNotification.id}`);
        await update(notificationRef, notificationData);
      } else {
        const notificationsRef = ref(db, "notifications");
        await push(notificationsRef, notificationData);
      }

      resetForm();
    } catch (error) {
      console.error("Error saving notification:", error);
    }
  };

  const handleEdit = (notification) => {
    setEditingNotification(notification);
    setFormData({
      title: notification.title,
      message: notification.message,
      type: notification.type,
      target: notification.target,
      schedule: notification.schedule,
      scheduledTime: notification.scheduledTime || "",
      conditions: notification.conditions || {
        userType: "all",
        minBalance: "",
        isVip: false,
        isActive: true
      }
    });
    setShowForm(true);
  };

  const handleDelete = async (notificationId) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar esta notificación?")) {
      try {
        const notificationRef = ref(db, `notifications/${notificationId}`);
        await remove(notificationRef);
      } catch (error) {
        console.error("Error deleting notification:", error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      message: "",
      type: "info",
      target: "all",
      schedule: "immediate",
      scheduledTime: "",
      conditions: {
        userType: "all",
        minBalance: "",
        isVip: false,
        isActive: true
      }
    });
    setEditingNotification(null);
    setShowForm(false);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "success": return <CheckCircle className="text-green-400" />;
      case "warning": return <AlertTriangle className="text-yellow-400" />;
      case "error": return <AlertTriangle className="text-red-400" />;
      default: return <Bell className="text-blue-400" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "success": return "bg-green-900/50 text-green-300 border-green-700";
      case "warning": return "bg-yellow-900/50 text-yellow-300 border-yellow-700";
      case "error": return "bg-red-900/50 text-red-300 border-red-700";
      default: return "bg-blue-900/50 text-blue-300 border-blue-700";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "sent": return "bg-green-900/50 text-green-300 border-green-700";
      case "pending": return "bg-yellow-900/50 text-yellow-300 border-yellow-700";
      case "scheduled": return "bg-purple-900/50 text-purple-300 border-purple-700";
      default: return "bg-slate-700 text-slate-300 border-slate-600";
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header y estadísticas */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestión de Notificaciones</h2>
          <p className="text-slate-400">Administra las notificaciones automáticas del sistema</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 shadow-lg"
        >
          <Plus size={16} />
          Nueva Notificación
        </button>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Total</p>
              <p className="text-3xl font-bold text-white">{stats.total}</p>
            </div>
            <div className="p-3 bg-blue-900/50 rounded-lg border border-blue-700">
              <Bell className="h-6 w-6 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Enviadas</p>
              <p className="text-3xl font-bold text-green-400">{stats.sent}</p>
            </div>
            <div className="p-3 bg-green-900/50 rounded-lg border border-green-700">
              <CheckCircle className="h-6 w-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Pendientes</p>
              <p className="text-3xl font-bold text-yellow-400">{stats.pending}</p>
            </div>
            <div className="p-3 bg-yellow-900/50 rounded-lg border border-yellow-700">
              <Clock className="h-6 w-6 text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Hoy</p>
              <p className="text-3xl font-bold text-purple-400">{stats.today}</p>
            </div>
            <div className="p-3 bg-purple-900/50 rounded-lg border border-purple-700">
              <Calendar className="h-6 w-6 text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Formulario de notificación */}
      {showForm && (
        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">
              {editingNotification ? "Editar Notificación" : "Nueva Notificación"}
            </h3>
            <button
              onClick={resetForm}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-400"
            >
              <X size={16} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Título
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-slate-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Tipo
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                >
                  <option value="info">Información</option>
                  <option value="success">Éxito</option>
                  <option value="warning">Advertencia</option>
                  <option value="error">Error</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Mensaje
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows={4}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-slate-400"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Público Objetivo
                </label>
                <select
                  value={formData.target}
                  onChange={(e) => setFormData({...formData, target: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                >
                  <option value="all">Todos los usuarios</option>
                  <option value="vip">Solo usuarios VIP</option>
                  <option value="free">Solo usuarios gratuitos</option>
                  <option value="active">Usuarios activos</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Programación
                </label>
                <select
                  value={formData.schedule}
                  onChange={(e) => setFormData({...formData, schedule: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                >
                  <option value="immediate">Enviar ahora</option>
                  <option value="scheduled">Programar</option>
                </select>
              </div>

              {formData.schedule === "scheduled" && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Fecha y Hora
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.scheduledTime}
                    onChange={(e) => setFormData({...formData, scheduledTime: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                    required
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center gap-2"
              >
                <Save size={16} />
                {editingNotification ? "Actualizar" : "Crear"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de notificaciones */}
      <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700">
        <div className="p-6 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">Notificaciones</h3>
        </div>

        <div className="divide-y divide-slate-700">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-slate-400">
              <Bell size={48} className="mx-auto mb-4 text-slate-600" />
              <p>No hay notificaciones configuradas</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div key={notification.id} className="p-6 hover:bg-slate-750 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-2 bg-slate-700 rounded-lg border border-slate-600">
                      {getTypeIcon(notification.type)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-semibold text-white">
                          {notification.title}
                        </h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTypeColor(notification.type)}`}>
                          {notification.type}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(notification.status)}`}>
                          {notification.status}
                        </span>
                      </div>

                      <p className="text-slate-300 mb-3">{notification.message}</p>

                      <div className="flex items-center gap-6 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Target size={14} />
                          {notification.target}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {formatTime(notification.createdAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={14} />
                          {notification.sentCount || 0} enviadas
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle size={14} />
                          {notification.readCount || 0} leídas
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(notification)}
                      className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-400"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(notification.id)}
                      className="p-2 hover:bg-red-900/50 rounded-lg transition-colors text-red-400"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
} 